import {
  faArrowsRotate,
  faThumbsDown,
  faThumbsUp,
} from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  Modal,
  FlexBox,
  SPACING,
  Typography,
  Slider,
  TextInput,
  JoonUIColor,
  Family,
  Shadow,
  JoonColorExpanded,
  QuestStatus,
} from "@joonapp/web-shared"
import { useQueryClient } from "@tanstack/react-query"
import dayjs from "dayjs"

import { useFirstQuestCompleteModalStore } from "./useFirstQuestCompleteModalStore"
import { useReviewQuestModalStore } from "./useReviewQuestModalStore"
import Counter from "../../components/counter/Counter"
import { QUERY_KEYS } from "../../constants"
import { useSubscription } from "../../hooks/useSubscription"
import { useFamilyQuery } from "../../networking/queries"
import { rejectQuest, retryQuest, verifyQuest } from "../../networking/quests"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../util/analytics"
import { localStorageItems } from "../../util/storage"
import { getUserFromFamily, isCoachQuest } from "../../util/util"
import {
  CoachUpsellModalType,
  useCoachUpsellModalStore,
} from "../care/CoachUpsellModal"
import useCareTeam from "../care/useCareTeam"
import CatchUpCardStatusOverlay from "../me/CatchUpCardStatusOverlay"
import TimeOfDayCard from "../me/TimeOfDayCard"

const ReviewQuestModal = () => {
  const {
    isOpen,
    onClose,
    quest,
    reward,
    setReward,
    notes,
    setNotes,
    difficulty,
    setDifficulty,
    error,
    setError,
    isLoading,
    setIsLoading,
    status,
    setStatus,
  } = useReviewQuestModalStore()
  const { onOpen: onOpenCoachUpsellModal } = useCoachUpsellModalStore()
  const { onOpen: onOpenFirstQuestCompleteModalIfApplicable } =
    useFirstQuestCompleteModalStore()

  const queryClient = useQueryClient()
  const { data: family } = useFamilyQuery()
  const { hasCoach } = useCareTeam()
  const { checkRemainingFreeQuests } = useSubscription()

  if (!quest) return null

  const child = getUserFromFamily(family as Family, quest?.user_id)
  const completionDate = dayjs.unix(Number(quest.completion_date))
  const completionText = `Completed ${completionDate?.format("MMMM D, YYYY")} at ${completionDate?.format("h:mm A")}`

  const onClickApprove = async () => {
    if (!quest) return
    const numCoachQuests = isCoachQuest(quest) ? 1 : 0
    checkRemainingFreeQuests({ numCoachQuests })
    setIsLoading(true)
    setStatus(QuestStatus.VERIFIED)
    setError("")
    const body = {
      verification_notes: notes,
      redeemable_reward: reward,
      completion_difficulty: difficulty,
      new_difficulty: undefined,
    }
    trackAnalyticEvent(ANALYTIC_EVENTS.VERIFY_QUEST)
    await verifyQuest({ instanceId: quest.id, data: body })
    await queryClient.invalidateQueries([QUERY_KEYS.QUEST_BOARD])
    queryClient.invalidateQueries([QUERY_KEYS.QUEST_REVIEW])
    queryClient.invalidateQueries([QUERY_KEYS.USER])
    queryClient.invalidateQueries([QUERY_KEYS.FAMILY])
    queryClient.invalidateQueries([
      QUERY_KEYS.COINS_READY_TO_REDEEM,
      quest.user_id,
    ])
    queryClient.invalidateQueries([
      QUERY_KEYS.COIN_CUMULATIVE_STATS,
      quest.user_id,
    ])
    queryClient.invalidateQueries([QUERY_KEYS.COIN_TRANSACTIONS, quest.user_id])
    onClose()
    setIsLoading(false)
    onOpenFirstQuestCompleteModalIfApplicable(child?.name)
  }

  const onClickReject = async () => {
    if (!quest) return
    const body = {
      verification_notes: notes,
      completion_difficulty: difficulty,
    }
    setIsLoading(true)
    setStatus(QuestStatus.REJECTED)
    setError("")
    await rejectQuest({ instanceId: quest.id, data: body })
    await queryClient.invalidateQueries([QUERY_KEYS.QUEST_BOARD])
    queryClient.invalidateQueries([QUERY_KEYS.QUEST_REVIEW])
    queryClient.invalidateQueries([QUERY_KEYS.USER])

    if (!hasCoach) {
      const lastSeenUpsellModalDate = localStorage.getItem(
        localStorageItems.rejectCoachUpsellModalSeenDate
      )
      const needsToSeeRejectCoachUpsellModal =
        !dayjs().isSame(dayjs(lastSeenUpsellModalDate), "week") ||
        !lastSeenUpsellModalDate
      if (needsToSeeRejectCoachUpsellModal)
        onOpenCoachUpsellModal(CoachUpsellModalType.reject)
    }
    onClose()
    setIsLoading(false)
  }

  const onClickRetry = async () => {
    if (!quest) return
    const body = {
      verification_notes: notes,
      completion_difficulty: difficulty,
    }
    setIsLoading(true)
    setStatus(QuestStatus.RETRY)
    setError("")
    await retryQuest({ instanceId: quest.id, data: body })
    await queryClient.invalidateQueries([QUERY_KEYS.QUEST_BOARD])
    queryClient.invalidateQueries([QUERY_KEYS.QUEST_REVIEW])
    queryClient.invalidateQueries([QUERY_KEYS.USER])
    onClose()
    setIsLoading(false)
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} animate displayCloseIcon>
      {status && <CatchUpCardStatusOverlay questStatusProp={status} />}
      <div
        className="quest-modal"
        style={{
          ...(isLoading
            ? {
                opacity: 0.5,
                pointerEvents: "none",
                userSelect: "none",
              }
            : {}),
        }}
      >
        <div className="px-4 pt-4 pb-3 border-b border-borderPrimary">
          <Typography variant="bodyBold" textAlign="left">
            Verify completed quest
          </Typography>
        </div>
        <div className="quest-modal-content bg-bgXLightGray !p-0 overflow-y-auto">
          <div
            className="p-4 pb-0"
            style={{
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
              alignItems: "center",
              gap: "12px",
              width: "100%",
            }}
          >
            <TimeOfDayCard
              date={quest.date}
              time={quest.series.reminder_time}
              title={quest.series.title}
              routine={quest.series.routine}
              isSelfInitiated={quest.series.assigner_profile === null}
              completionText={completionText}
            />
          </div>
          {quest.completion_photo_url && (
            <div className="mt-3">
              <img
                src={quest.completion_photo_url}
                alt="Verification"
                className="w-full h-full object-cover"
              />
            </div>
          )}
          {quest.completion_notes && (
            <div className="flex flex-col gap-1 p-4">
              <Typography variant="bodySmallBold">Completion note</Typography>
              <Typography variant="bodySmall">
                "{quest.completion_notes}"
              </Typography>
            </div>
          )}
          {quest.series.description && (
            <div className="flex flex-col gap-1 p-4">
              <Typography variant="bodySmallBold">Quest description</Typography>
              <Typography variant="bodySmall">
                {quest.series.description}
              </Typography>
            </div>
          )}
          <div className="w-full bg-white rounded-t-lg px-6 py-4 flex flex-col gap-4">
            <div>
              <Typography
                variant="bodySmallBold"
                textAlign="center"
                style={{ marginBottom: SPACING.space1 }}
              >
                How difficult was it to motivate {child?.name}?
              </Typography>
              <Slider
                value={difficulty || 0}
                options={[0, 1, 2, 3, 4, 5, 6]}
                setValue={setDifficulty}
                minLabel="Very easy"
                maxLabel="Very difficult"
              />
            </div>
            <div>
              <Typography variant="bodySmallBold" textAlign="center">
                Reward Adjustment
              </Typography>
              <Typography
                variant="bodySmall"
                textAlign="center"
                color={JoonUIColor.text.secondary}
                style={{ marginBottom: SPACING.space1 }}
              >
                Modify rewards based on completion quality
              </Typography>
              <Counter value={reward || 0} setValue={setReward} />
            </div>
            <FlexBox direction="column" gap={SPACING.space1}>
              <Typography variant="bodySmallBold" textAlign="center">
                Add a note for {child?.name}
              </Typography>
              <TextInput
                name="Note"
                placeholder="Type a message..."
                value={notes || ""}
                onChange={(e) => setNotes(e.target.value)}
                fullWidth
                maxLength={255}
              />
            </FlexBox>
          </div>
          {error && (
            <Typography
              variant="bodySmallBold"
              color={JoonUIColor.semantic.alert}
              style={{ marginTop: SPACING.space1 }}
            >
              {error}
            </Typography>
          )}
          <CatchUpCardStatusOverlay />
          <div className="flex flex-row justify-center items-center sticky bg-backgroundPrimary bottom-0 p-3 border-t border-borderPrimary shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
            <FlexBox
              direction="column"
              align="center"
              gap={SPACING.space2}
              style={{ flex: 0.5, cursor: "pointer" }}
              onClick={onClickReject}
            >
              <FontAwesomeIcon
                icon={faThumbsDown}
                color={JoonUIColor.semantic.alert}
                fontSize="24px"
              />
              <Typography variant="bodyBold" color={JoonUIColor.icon.neutral}>
                Reject
              </Typography>
            </FlexBox>
            <FlexBox
              direction="column"
              align="center"
              gap={SPACING.space2}
              style={{ flex: 0.5, cursor: "pointer" }}
              onClick={onClickRetry}
            >
              <FontAwesomeIcon
                icon={faArrowsRotate}
                color={JoonUIColor.icon.accent}
                fontSize="24px"
              />
              <Typography variant="bodyBold" color={JoonUIColor.icon.neutral}>
                Retry
              </Typography>
            </FlexBox>
            <FlexBox
              direction="column"
              align="center"
              gap={SPACING.space2}
              onClick={onClickApprove}
              style={{
                padding: `${SPACING.space2} ${SPACING.space4}`,
                borderRadius: SPACING.space2,
                background: JoonColorExpanded.viridian100,
                boxShadow: Shadow.medium,
                cursor: "pointer",
                flex: 1,
              }}
            >
              <FontAwesomeIcon
                icon={faThumbsUp}
                color={JoonUIColor.semantic.success}
                fontSize="24px"
              />
              <Typography
                variant="bodyBold"
                color={JoonUIColor.semantic.success}
              >
                Approve
              </Typography>
            </FlexBox>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default ReviewQuestModal
